/**
 * File: effort.ts
 * Path: packages/config/src/effort/effort.ts
 * Description: Time and rand costs spent on a lead or client, and what they
 * come to. Time entries record who did the work and the hourly rate then, so
 * changing a rate later never rewrites old costs. All money is whole cents.
 * The "cost to win" on a lead or client, and CAC on the Numbers page, are
 * built from these.
 */

import { readMillis, readNumber, readText } from "../leads/leads";

/** What a lead or client record is: entries are kept in one collection each. */
export type EffortRecordKind = "lead" | "client";

export const TIME_KINDS = ["research", "outreach", "meeting", "quote", "admin", "other"] as const;
export type TimeKind = (typeof TIME_KINDS)[number];
export const TIME_KIND_LABELS: Record<TimeKind, string> = {
  research: "Research",
  outreach: "Outreach",
  meeting: "Meeting",
  quote: "Quote",
  admin: "Admin",
  other: "Other",
};

export const COST_KINDS = ["meeting", "travel", "printing", "tools", "other"] as const;
export type CostKind = (typeof COST_KINDS)[number];
export const COST_KIND_LABELS: Record<CostKind, string> = {
  meeting: "Meeting",
  travel: "Travel",
  printing: "Printing",
  tools: "Tools",
  other: "Other",
};

/** Liam's day-job rate, R75 an hour, until he sets one. */
export const DEFAULT_HOURLY_RATE_CENTS = 7500;
/** Longest single entry: a full day. */
export const MAX_ENTRY_MINUTES = 24 * 60;

export const toCents = (rand: number): number => Math.round(rand * 100);

/** The cost of `minutes` of work at an hourly rate, in whole cents. */
export const timeCostCents = (minutes: number, hourlyRateCents: number): number =>
  Math.round((minutes * hourlyRateCents) / 60);

/** 75 → "1h 15m"; 45 → "45m"; 120 → "2h". */
export const formatMinutes = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

export interface TimeEntry {
  id: string;
  recordKind: EffortRecordKind;
  recordId: string;
  /** The client job (clients/<id>/projects/<jobId>) the time was spent on, when one is named. */
  jobId: string | null;
  minutes: number;
  kind: TimeKind;
  note: string;
  /** YYYY-MM-DD the work was done. */
  date: string;
  byUid: string;
  byEmail: string;
  /** Hourly rate, in cents, when it was logged. */
  rateCents: number;
  costCents: number;
  createdAt: number | null;
}

export interface CostEntry {
  id: string;
  recordKind: EffortRecordKind;
  recordId: string;
  amountCents: number;
  kind: CostKind;
  note: string;
  date: string;
  byUid: string;
  byEmail: string;
  createdAt: number | null;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const wholeNumber = (v: unknown): number | null => {
  const n = readNumber(v);
  return n !== null && Number.isInteger(n) ? n : null;
};

/** Reads a stored time entry; anything unreadable gives null. */
export const toTimeEntry = (id: string, data: Record<string, unknown>): TimeEntry | null => {
  const minutes = wholeNumber(data.minutes);
  const rateCents = wholeNumber(data.rateCents);
  const costCents = wholeNumber(data.costCents);
  const date = readText(data.date);
  if (minutes === null || minutes < 1 || rateCents === null || rateCents < 0 || costCents === null || costCents < 0) return null;
  if (!ISO_DATE.test(date)) return null;
  return {
    id,
    recordKind: data.recordKind === "client" ? "client" : "lead",
    recordId: readText(data.recordId),
    jobId: readText(data.jobId) || null,
    minutes,
    kind: TIME_KINDS.find((k) => k === data.kind) ?? "other",
    note: readText(data.note),
    date,
    byUid: readText(data.byUid),
    byEmail: readText(data.byEmail),
    rateCents,
    costCents,
    createdAt: readMillis(data.createdAt),
  };
};

export const toCostEntry = (id: string, data: Record<string, unknown>): CostEntry | null => {
  const amountCents = wholeNumber(data.amountCents);
  const date = readText(data.date);
  if (amountCents === null || amountCents < 1 || !ISO_DATE.test(date)) return null;
  return {
    id,
    recordKind: data.recordKind === "client" ? "client" : "lead",
    recordId: readText(data.recordId),
    amountCents,
    kind: COST_KINDS.find((k) => k === data.kind) ?? "other",
    note: readText(data.note),
    date,
    byUid: readText(data.byUid),
    byEmail: readText(data.byEmail),
    createdAt: readMillis(data.createdAt),
  };
};

export interface EffortSummary {
  minutes: number;
  timeCents: number;
  costCents: number;
  /** Time at the logged rates plus rand costs: what it cost to win them. */
  totalCents: number;
}

export const summariseEffort = (
  time: readonly Pick<TimeEntry, "minutes" | "costCents">[],
  costs: readonly Pick<CostEntry, "amountCents">[],
): EffortSummary => {
  const minutes = time.reduce((sum, t) => sum + t.minutes, 0);
  const timeCents = time.reduce((sum, t) => sum + t.costCents, 0);
  const costCents = costs.reduce((sum, c) => sum + c.amountCents, 0);
  return { minutes, timeCents, costCents, totalCents: timeCents + costCents };
};

// ─── Hourly rates ────────────────────────────────────────────────────

/** Someone's own rate when they have one; everyone else gets the default. */
export interface StaffRate {
  email: string;
  rateCents: number;
}

export interface RateSettings {
  defaultHourlyRateCents: number;
  rates: StaffRate[];
}

export const MAX_STAFF_RATES = 20;
/** R5,000 an hour: a typo guard, not a real limit. */
export const MAX_HOURLY_RATE_CENTS = 500_000;

/** The rate for whoever does the work: theirs, else the default (R75 an hour). */
export const hourlyRateFor = (settings: RateSettings, email: string): number => {
  const key = email.trim().toLowerCase();
  const own = settings.rates.find((r) => r.email === key);
  return own ? own.rateCents : settings.defaultHourlyRateCents;
};

export const toRateSettings = (data: Record<string, unknown> | null | undefined): RateSettings => {
  const def = wholeNumber(data?.defaultHourlyRateCents);
  const list = Array.isArray(data?.rates) ? (data?.rates as unknown[]) : [];
  const rates: StaffRate[] = [];
  for (const item of list.slice(0, MAX_STAFF_RATES)) {
    if (typeof item !== "object" || item === null) continue;
    const row = item as Record<string, unknown>;
    const email = readText(row.email).toLowerCase();
    const rateCents = wholeNumber(row.rateCents);
    if (email && rateCents !== null && rateCents >= 0 && rateCents <= MAX_HOURLY_RATE_CENTS) {
      rates.push({ email, rateCents });
    }
  }
  return {
    defaultHourlyRateCents:
      def !== null && def >= 0 && def <= MAX_HOURLY_RATE_CENTS ? def : DEFAULT_HOURLY_RATE_CENTS,
    rates,
  };
};

// ─── Shared monthly costs ───────────────────────────────────────────

/** Where a shared cost goes: winning clients, delivering work, or overheads. */
export const SHARED_COST_BUCKETS = ["winning", "delivering", "overhead"] as const;
export type SharedCostBucket = (typeof SHARED_COST_BUCKETS)[number];
export const SHARED_COST_BUCKET_LABELS: Record<SharedCostBucket, string> = {
  winning: "Winning clients",
  delivering: "Delivering work",
  overhead: "Overheads",
};

export interface SharedCostItem {
  label: string;
  amountCents: number;
  bucket: SharedCostBucket;
}

export interface AdSpendItem {
  channel: string;
  amountCents: number;
}

/** crmCosts/<YYYY-MM>: costs that belong to no single lead, and ad spend. */
export interface MonthlyCosts {
  /** YYYY-MM */
  month: string;
  items: SharedCostItem[];
  adSpend: AdSpendItem[];
}

export const MAX_MONTHLY_LINES = 40;

export const toMonthlyCosts = (month: string, data: Record<string, unknown> | null | undefined): MonthlyCosts => {
  const rows = (v: unknown): Record<string, unknown>[] =>
    Array.isArray(v) ? (v.slice(0, MAX_MONTHLY_LINES).filter((r) => typeof r === "object" && r !== null) as Record<string, unknown>[]) : [];
  return {
    month,
    items: rows(data?.items).flatMap((r) => {
      const amountCents = wholeNumber(r.amountCents);
      const label = readText(r.label);
      if (!label || amountCents === null || amountCents < 0) return [];
      return [
        {
          label,
          amountCents,
          bucket: SHARED_COST_BUCKETS.find((b) => b === r.bucket) ?? "overhead",
        },
      ];
    }),
    adSpend: rows(data?.adSpend).flatMap((r) => {
      const amountCents = wholeNumber(r.amountCents);
      const channel = readText(r.channel);
      return channel && amountCents !== null && amountCents >= 0 ? [{ channel, amountCents }] : [];
    }),
  };
};

export const sharedCostTotals = (costs: MonthlyCosts): Record<SharedCostBucket, number> & { adSpend: number } => {
  const totals = { winning: 0, delivering: 0, overhead: 0, adSpend: 0 };
  for (const item of costs.items) totals[item.bucket] += item.amountCents;
  for (const ad of costs.adSpend) totals.adSpend += ad.amountCents;
  return totals;
};

